import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  OnInit,
  output,
  signal,
  untracked,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { deepComputed } from '@ngrx/signals';
import { TranslateModule } from '@ngx-translate/core';
import { isNil } from 'lodash';
import { filter, map, of, switchMap, tap } from 'rxjs';

import { KeyValueIsDisabledModel, KeyValueRoleModel, KeyValueType } from '@models/common';
import { Role } from '@models/module/main';
import { DOCUMENT_ACTIONSD_DATA_DEFAULTS, DocumentActionsData } from '@module-definition/document/common';
import { EntityInstanceTransition } from '@module-instance/dynamic-module/common';
import { ModuleInstanceService, StateAssignmentService } from '@module-instance/services';
import { CipoSelectComponent } from '@shared/components/fields/cipo-select/cipo-select.component';
import { CipoFieldTypes, CipoListConfig } from '@shared/components/fields/common';
import { DropdownActionsEnum, DropdownItem } from '@shared/consts';
import { ConfirmationDialog } from '@shared/dialogs';
import { NotificationService, UserService } from '@shared/services';

@Component({
  selector: 'cipo-document-actions',
  imports: [MatIconModule, MatButtonModule, MatMenuModule, TranslateModule, FlexLayoutModule, CipoSelectComponent],
  templateUrl: './document-actions.component.html',
  styles: [
    `
      .input-container {
        width: 212px;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentActionsComponent implements OnInit {
  private notify = inject(NotificationService);
  private dialog = inject(MatDialog);
  private moduleInstanceService = inject(ModuleInstanceService);
  private stateAssignmentService = inject(StateAssignmentService);

  private userService = inject(UserService);
  data = input<DocumentActionsData>({});

  /** Transitions for the document. Displayed as dropdown of "Actions" button */
  transitions = input<(EntityInstanceTransition & { disabled?: boolean })[]>([]);

  /** Actions to be displayed in the dropdown menu */
  actions = input<DropdownItem[]>([]);

  /** Workflow actions displayed alongside transitions in the Actions menu. */
  workflowActions = input<DropdownItem[]>([]);

  deepData = deepComputed(() => ({ ...DOCUMENT_ACTIONSD_DATA_DEFAULTS, ...this.data() }));

  /** Emits when a transition is clicked (from save-button) */
  transitionClicked = output<EntityInstanceTransition>();

  /** Emits when an action is clicked (from more_vert) */
  actionClicked = output<DropdownActionsEnum>();

  /** Emits when one of an action's options is picked, for the actions rendered as a submenu. */
  actionOptionClicked = output<{ id: DropdownActionsEnum; optionKey: number }>();

  /** Emits when the close action is triggered */
  cancelRequested = output<void>();

  /** Emits when the submit button is clicked */
  submitRequested = output<void>();

  /** Emits when the assignment is changed for a document. */
  assignmentChanged = output<KeyValueRoleModel<string, string>>();

  /** Emits when the document number is changed for a document IF alwaysShowDocNum: true.
   * For other cases, this component handles the change (if it's accessible and enabled) */
  docNumChanged = output<KeyValueType<number>>();

  disableActions = computed(() => this.data().disableActions || false);
  /* if disableDocNum is true, it means it's a preset. If showCancel is true (transition or editing an open document), hide the doc */
  showDocNum = linkedSignal(() => {
    const { alwaysShowDocNum, disableDocNum, hideDocNum, showCancel } = this.deepData;
    // show doc num only if there are available numbers to chose from
    return (
      !hideDocNum() && !showCancel() && ((alwaysShowDocNum() && this.docNumOptions().length > 0) || disableDocNum())
    );
  });

  DropdownActionsEnum = DropdownActionsEnum;

  assignControl = new FormControl(this.data().assignee?.key ?? null);

  /* cipo select waits for string keys, that's why we need to convert it to string.
  Remove the conversion if we will have number keys for all lists, and / or will update cipo-select to use number keys */
  docNumControl = new FormControl(this.data().documentNumber?.key?.toString() ?? null);

  docNumOptions = signal<KeyValueIsDisabledModel[]>([]);
  fieldsData = linkedSignal(() => {
    const { assignOptions, assignee, entityInstanceId, alwaysShowDocNum } = this.deepData;
    /* Currently this condition is ok for checking draft */
    const isDraft = !!entityInstanceId() && alwaysShowDocNum();
    return {
      assign: {
        type: CipoFieldTypes.Select,
        label: 'roles.changeAssignment',
        hideNullOption: true,
        options: assignOptions() || (assignee() ? [assignee()] : []),
      } as CipoListConfig,
      documentNumber: {
        type: CipoFieldTypes.Select,
        label: 'dm.details.docNum',
        noneLabel: 'general.auto',
        fieldDescription: isDraft ? 'dm.details.draftDocNum' : undefined,
        canSelectNullableOptions: true,
        options: this.docNumOptions(),
      } as CipoListConfig,
    };
  });

  constructor() {
    let loadingDocNums = false;
    effect(() => {
      /* disableDocNum is true for presets */
      if (!this.deepData.hideDocNum() && !this.deepData.disableDocNum()) {
        if (loadingDocNums) {
          return;
        }
        loadingDocNums = true;
        untracked(() => this.loadDocumentNumberOptions());
      }
    });

    effect(() => this.assignControl.setValue(this.deepData.assignee()?.key, { emitEvent: false }));

    /* Enable / disable assign control */
    effect(() => {
      const canAssign = !this.deepData.hideAssign() && this.deepData.canAssign() && !this.deepData.disableActions();
      this.toggleControlState(this.assignControl, canAssign);
    });

    effect(() => {
      if (this.deepData.disableMoreActions()) {
        this.showDocNum.set(false);
      }
    });

    /* Enable / disable num control */
    effect(() => {
      const canChangeDocNum =
        !this.deepData.hideDocNum() && !this.deepData.disableDocNum() && !this.deepData.disableActions();
      this.toggleControlState(this.docNumControl, canChangeDocNum);
    });

    /* Set the initial doc num for presets */
    effect(() => {
      const docNum = this.deepData.documentNumber();
      if (docNum?.key) {
        const stringDocNum = docNum.key.toString();
        this.docNumControl.setValue(stringDocNum, { emitEvent: false });
        if (untracked(() => this.data().disableDocNum)) {
          this.docNumOptions.set([{ key: stringDocNum, value: docNum.value, isDisabled: true }]);
        }
      }
    });
  }

  ngOnInit(): void {
    this.handleAssignmentChange();
    this.handleNumberChange();
  }

  private toggleControlState(control: FormControl, enabled: boolean) {
    if (control.enabled !== enabled) {
      if (enabled) {
        control.enable({ emitEvent: false });
      } else {
        control.disable({ emitEvent: false });
      }
    }
  }

  private loadDocumentNumberOptions() {
    const { moduleId, entityInstanceId, contractId } = this.data();
    this.moduleInstanceService
      .getAvailableDocumentNumberDict({ moduleId, entityInstanceId, contractId })
      .subscribe(docNumbers => {
        if (!docNumbers?.length) {
          this.showDocNum.set(false);
          return;
        }
        const options: KeyValueIsDisabledModel[] = docNumbers.map(({ key, value, isPreset }) => ({
          key: key.toString(),
          value,
          isDisabled: isPreset,
        }));
        this.docNumOptions.set(options);
      });
  }

  private handleAssignmentChange() {
    this.assignControl.valueChanges.subscribe(assignee => {
      const options = this.fieldsData().assign.options || [];
      const role = options.find(op => op.key === assignee) as KeyValueRoleModel<string, string>;
      const { contractId, moduleId, entityInstanceId, transitionStarted } = this.data();
      if (transitionStarted) {
        /* If the transition is started, the role in sent in the transition request. No confirmation needed, as the assignment can be changed again */
        this.assignmentChanged.emit(role);
        return;
      }
      this.dialog
        .open(ConfirmationDialog, {
          panelClass: ['cipo-dialog', 'classic'],
          ...this.userService.getResponsiveDialogSize().xs,
          data: {
            title: 'roles.changeAssignment',
            description: 'dm.details.confirmAssignment',
            params: { assignee: role.value },
            yesLabel: 'actions.confirm',
            noLabel: 'actions.cancel',
          },
        })
        .afterClosed()
        .subscribe(confirmed => {
          if (!confirmed) {
            this.assignControl.setValue(this.data().assignee?.key, { emitEvent: false });
            return;
          }

          this.stateAssignmentService
            .assignUser({
              contractId,
              moduleId,
              entityInstanceId,
              role,
            })
            .subscribe(() => {
              this.notify.success('messages.documentAssigned');
              this.assignmentChanged.emit(role);
            });
        });
    });
  }

  private handleNumberChange() {
    this.docNumControl.valueChanges
      .pipe(
        switchMap(documentNumber => {
          if (this.data().alwaysShowDocNum) {
            return of(documentNumber);
          }
          const docNum = documentNumber
            ? this.docNumOptions().find(op => op.key === documentNumber.toString()).value
            : 'Auto';
          return this.dialog
            .open(ConfirmationDialog, {
              panelClass: ['cipo-dialog', 'classic'],
              ...this.userService.getResponsiveDialogSize().xs,
              data: {
                title: 'actions.changeDocNum',
                description: 'dm.details.confirmDocNum',
                params: { docNum },
                yesLabel: 'actions.confirm',
                noLabel: 'actions.cancel',
              },
            })
            .afterClosed()
            .pipe(
              tap(confirmed => {
                if (!confirmed) {
                  this.docNumControl.setValue(null, { emitEvent: false });
                }
              }),
              filter(confirmed => !!confirmed),
              map(() => documentNumber),
            );
        }),
      )
      .subscribe(docNum => {
        const { entityInstanceId, alwaysShowDocNum } = this.data();
        const documentNumber = this.docNumOptions().find(op => op.key === docNum);
        const numberDocNum = { key: Number(docNum), value: documentNumber.value };
        if (alwaysShowDocNum) {
          this.docNumChanged.emit(numberDocNum);
        } else {
          this.docNumControl.disable({ emitEvent: false });
          this.moduleInstanceService
            .updateDocumentNumber({ documentNumber: numberDocNum.key ?? null, entityInstanceId })
            .subscribe(() => {
              this.notify.success('messages.documentNumberChanged');
              this.docNumControl.enable({ emitEvent: false });
              this.showDocNum.set(false);
              this.docNumChanged.emit(numberDocNum);
            });
        }
      });
  }

  handleActionClick(action: DropdownActionsEnum) {
    switch (action) {
      case DropdownActionsEnum.CHANGE_DOC_NUM:
        if (this.data().hideDocNum) {
          return;
        }
        const { key, value } = this.data().documentNumber;
        if (!isNil(key) && !this.docNumOptions().find(op => op.key === this.docNumControl.value)) {
          this.docNumOptions.update(options => {
            return [{ key: key?.toString(), value, isDisabled: false }, ...options];
          });
        }
        this.showDocNum.set(true);
        break;
      default:
        this.actionClicked.emit(action);
        break;
    }
  }

  handleTransitionClick(transitionId: number, role?: Role) {
    const transition = this.transitions().find(t => t.id === transitionId);
    if (!transition) {
      return;
    }
    this.transitionClicked.emit({ ...transition, roles: role ? [role] : transition.roles });
  }
}
